import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Shield,
  Users,
  MapPin,
  AlertTriangle,
  Phone,
  Search,
  UserCheck,
  CreditCard,
  FileText,
  Star,
  Camera,
  RefreshCw,
  Lock,
  Trash2,
  Info,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import "./PrivacySafetyPolicy.css";

const PrivacySafetyPolicy = () => {
  const [activeTab, setActiveTab] = useState("all");
  const [activeCardId, setActiveCardId] = useState(null);

  const toggleCard = (id) => {
    setActiveCardId((prev) => (prev === id ? null : id));
  };

  const policyData = [
    {
      id: "child-1",
      category: "child-safety",
      icon: <Users className="policy-icon" />,
      color: "blue",
      title: "Parental Responsibility",
      shortDesc: "All children under 18 must be supervised by a parent or guardian at all times.",
      fullDesc:
        "All children under the age of 18 must be accompanied and supervised by a parent, legal guardian, or a responsible adult designated by the parent. TPL does not provide child supervision or childcare services. Guardians are expected to stay close to their children at all times throughout the event.",
    },
    {
      id: "child-2",
      category: "child-safety",
      icon: <MapPin className="policy-icon" />,
      color: "green",
      title: "Prevention Guidelines",
      shortDesc: "Proactive measures to keep children safe during the event.",
      fullDesc: (
        <ul className="policy-list">
          <li>Keep children within sight at all times</li>
          <li>Set a designated meeting spot in case of separation</li>
          <li>Provide your child with an ID tag including your name and contact number</li>
          <li>Brief your child about staying in visible areas</li>
        </ul>
      ),
    },
    {
      id: "child-3",
      category: "child-safety",
      icon: <AlertTriangle className="policy-icon" />,
      color: "orange",
      title: "Missing Child Protocol",
      shortDesc: "Steps taken when a child is reported missing.",
      fullDesc:
        "If a missing child is reported, Aattum TPL staff may assist by broadcasting internal alerts with basic child descriptions, notifying event security to monitor exits, and contacting local law enforcement if deemed necessary. However, this assistance is discretionary and does not constitute an assumption of responsibility.",
    },
    {
      id: "child-4",
      category: "child-safety",
      icon: <Phone className="policy-icon" />,
      color: "purple",
      title: "Emergency Contact Procedure",
      shortDesc: "How to reach help immediately during emergencies.",
      fullDesc:
        "In case of an emergency, parents should immediately approach the nearest TPL help desk or event staff. Emergency contact numbers will be displayed across all major event zones for quick communication and coordination.",
    },
    {
      id: "child-5",
      category: "child-safety",
      icon: <Search className="policy-icon" />,
      color: "teal",
      title: "Lost & Found Assistance",
      shortDesc: "Dedicated booth for reporting missing persons or belongings.",
      fullDesc:
        "Aattum TPL maintains a dedicated Lost & Found booth at the venue for reporting and locating missing persons or belongings. Parents are advised to promptly notify this booth if their child is missing, to help facilitate quick action and communication.",
    },
    {
      id: "reg-1",
      category: "registration",
      icon: <UserCheck className="policy-icon" />,
      color: "blue",
      title: "Eligibility Requirements",
      shortDesc: "Criteria participants must meet to register.",
      fullDesc:
        "Participants must meet the age, skill, and category criteria as defined by the specific competition rules. Proof of identity or eligibility may be requested at any time during the registration or event period.",
    },
    {
      id: "reg-2",
      category: "registration",
      icon: <FileText className="policy-icon" />,
      color: "green",
      title: "Registration Process",
      shortDesc: "How to complete your registration officially.",
      fullDesc:
        "All registrations must be completed through the official Aattum TPL website or authorized platforms. Accurate and complete information is required. Registration is confirmed only after payment and confirmation email receipt.",
    },
    {
      id: "reg-3",
      category: "registration",
      icon: <CreditCard className="policy-icon" />,
      color: "purple",
      title: "Payment & No Refund Policy",
      shortDesc: "All fees are non-refundable once payment is made.",
      fullDesc:
        "Fees must be paid in full at registration via credit/debit card or bank transfer. All registration and league fees are non-refundable. No refunds will be issued for scheduling conflicts, personal emergencies, injury, illness, or event cancellation due to force majeure. Transfers of registration are not permitted unless officially approved.",
    },
    {
      id: "reg-4",
      category: "registration",
      icon: <Star className="policy-icon" />,
      color: "yellow",
      title: "Code of Conduct",
      shortDesc: "Expected behavior standards for all participants.",
      fullDesc:
        "All participants must follow Aattum TPL's code of conduct at all times. Any form of disrespect, misconduct, or violations may result in immediate disqualification without refund.",
    },
    {
      id: "reg-5",
      category: "registration",
      icon: <Camera className="policy-icon" />,
      color: "indigo",
      title: "Media Release Consent",
      shortDesc: "Your agreement for promotional use of images and videos.",
      fullDesc:
        "By registering, you grant Aattum TPL the right to use your image, video footage, and performance media for promotional, marketing, and archival purposes across digital and print platforms.",
    },
    {
      id: "privacy-1",
      category: "privacy",
      icon: <Lock className="policy-icon" />,
      color: "gray",
      title: "Data Privacy & Security",
      shortDesc: "How we protect your personal information.",
      fullDesc:
        "Aattum TPL is committed to protecting your personal information. All data collected during registration will be securely stored and used only for official Talented Premier League operations in compliance with applicable data protection laws. We do not sell or share your data with third parties without consent.",
    },
    {
      id: "privacy-2",
      category: "privacy",
      icon: <RefreshCw className="policy-icon" />,
      color: "teal",
      title: "Policy Updates",
      shortDesc: "We may update policies; you'll be notified of major changes.",
      fullDesc:
        "TPL reserves the right to update these policies at any time to reflect operational or legal changes. Significant changes will be communicated to registered participants via email or official announcements.",
    },
    {
      id: "privacy-3",
      category: "privacy",
      icon: <Trash2 className="policy-icon" />,
      color: "red",
      title: "Data Retention and Deletion",
      shortDesc: "Learn how long we keep your data and how to request deletion.",
      fullDesc: (
        <div className="policy-content">
          <p>We retain user data only for as long as necessary to provide our services.</p>
          <p>
            Users have the right to request deletion of their personal data at any time. 
            To request data deletion, users can contact us at:
          </p>
          <p style={{ marginTop: "10px", fontWeight: "bold" }}>Email: admin@aattumtpl.com</p>
          <p style={{ marginTop: "10px" }}>
            Upon receiving a request, we will delete the user’s data from our systems within a reasonable timeframe.
          </p>
          <p style={{ marginTop: "10px", fontStyle: "italic", fontSize: "0.9em" }}>
            If required, certain data may be retained for legal or regulatory purposes.
          </p>
        </div>
      ),
    },
  ];

  const filteredPolicies =
    activeTab === "all"
      ? policyData
      : policyData.filter((p) => p.category === activeTab);

  return (
    <div className="privacy-page">
      <motion.div
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="privacy-header"
      >
        <div className="header-icon-wrapper">
          <Shield size={48} className="header-shield" />
        </div>
        <h1>Aattum Talented Premier League</h1>
        <p>Privacy, Safety & Registration Policy</p>
        <div className="header-line"></div>
      </motion.div>

      <motion.div
        className="important-notice"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.3 }}
      >
        <Info className="notice-icon" />
        <div className="notice-content">
          <h3>Important Notice</h3>
          <p>
            Parents and guardians are fully responsible for the supervision and
            safety of their children at all times during the event. Aattum
            Talented Premier League is not responsible for any incidents
            involving missing children. By registering, you agree to all terms
            outlined below.
          </p>
        </div>
      </motion.div>

      <div className="tab-navigation">
        <button
          className={`tab-btn ${activeTab === "all" ? "active" : ""}`}
          onClick={() => { setActiveTab("all"); setActiveCardId(null); }}
        >
          All Policies
        </button>
        <button
          className={`tab-btn ${activeTab === "child-safety" ? "active" : ""}`}
          onClick={() => { setActiveTab("child-safety"); setActiveCardId(null); }}
        >
          Child Safety
        </button>
        <button
          className={`tab-btn ${activeTab === "registration" ? "active" : ""}`}
          onClick={() => { setActiveTab("registration"); setActiveCardId(null); }}
        >
          Registration
        </button>
        <button
          className={`tab-btn ${activeTab === "privacy" ? "active" : ""}`}
          onClick={() => { setActiveTab("privacy"); setActiveCardId(null); }}
        >
          Privacy & Data
        </button>
      </div>

      <div className="policy-grid">
        {filteredPolicies.map((policy, index) => (
          <motion.div
            key={policy.id}
            className={`policy-card ${policy.color} ${activeCardId === policy.id ? "expanded" : ""
              }`}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.08 }}
            viewport={{ once: true }}
          >
            <div className="card-header">
              <div className="icon-wrapper">{policy.icon}</div>
              <div className="title-section">
                <h3>{policy.title}</h3>
                <p className="short-desc">{policy.shortDesc}</p>
              </div>
              <button
                className="expand-btn"
                onClick={() => toggleCard(policy.id)}
                aria-label="Toggle details"
              >
                {activeCardId === policy.id ? (
                  <ChevronUp size={20} />
                ) : (
                  <ChevronDown size={20} />
                )}
              </button>
            </div>

            <motion.div
              className="card-body"
              initial={false}
              animate={{
                height: activeCardId === policy.id ? "auto" : 0,
                opacity: activeCardId === policy.id ? 1 : 0,
              }}
              transition={{ duration: 0.3 }}
            >
              <div className="full-desc">{policy.fullDesc}</div>
            </motion.div>
          </motion.div>
        ))}
      </div>

      <motion.div
        className="footer-disclaimer"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        viewport={{ once: true }}
      >
        <Shield className="footer-icon" />
        <p>
          By participating in Aattum Talented Premier League, you acknowledge
          that you have read, understood, and agree to abide by all policies
          outlined above. For questions or concerns, please contact our support
          team.
        </p>
      </motion.div>
    </div>
  );
};

export default PrivacySafetyPolicy;
